import { generatePath, matchPath } from "react-router";
import lodash from "lodash";
import { getGroupType } from "src/util/groupType";
import { stripFormatCharacters } from "src/util/stripFormatCharacters";

const URL = {
  NEW_USER_LANDING: "/start",
  INSIGHTS: "/:workspaceId?/insights",
  SUMMARY_LEGACY: "/:workspaceId?/summary",
  SEARCH: "/:workspaceId?/search/:type/:searchTerm?",
  // Before PEOPLE so /workforce/people is not parsed as workspaceId=workforce
  // (matchPaths / workspaceIdFromUrl walk pathList from lodash.values(URL)).
  WORKFORCE_ROOT: "/:workspaceId?/workforce",
  WORKFORCE: "/:workspaceId?/workforce/:tabType",
  PEOPLE: "/:workspaceId?/people",
  TAG_EXPLORE: "/:workspaceId?/explore/tags/:id",
  TEAM_EXPLORE: "/:workspaceId?/explore/teams/:id",
  GOALS_EXPLORE: "/:workspaceId?/explore/goals/:id?",
  EXPLORE: "/:workspaceId?/explore",
  USER_HOME_BASE_PATH: "/:workspaceId?/user",
  USER_HOME: "/:workspaceId?/user/:tabType",
  ORG_DESIGN_HOME: "/:workspaceId?/org-design-home",
  TEAMS: "/:workspaceId?/teams",
  TEAM_DETAILS_BASE_PATH: "/:workspaceId?/teams/:id",
  TEAM_DETAILS: "/:workspaceId?/teams/:id/:tabType",
  PERSON_DETAILS: "/:workspaceId?/people/:id/:tabType",
  PERSON_DETAILS_EMAIL_LINK: "/:workspaceId?/people-link/:email",
  PERSON_DETAILS_BASE_PATH: "/:workspaceId?/people/:id",
  PERSON_DETAILS_FROM_DIRECTORY: "/:workspaceId?/people/:id",
  POSITION_DETAILS: "/:workspaceId?/positions/:id/:tabType",
  POSITION_DETAILS_BASE_PATH: "/:workspaceId?/positions/:id",
  WORKFORCE_PLANNING_DASHBOARD: "/:workspaceId?/workforce-planning-dashboard",
  DATASOURCE: "/:workspaceId?/configuration/datasource/:dataSourceId",
  WORKSPACES: "/:workspaceId?/workspaces",
  WORKSPACE_SETTINGS_ROOT: "/:workspaceId?/workspace-settings",
  WORKSPACE_SETTINGS: "/:workspaceId?/workspace-settings/:tabType",
  WORKSPACE_CONFIGURATION:
    "/:workspaceId?/configuration/:tabType/:actionType?/:integrationType?",
  CREATE_WORKSPACE: "/workspaces/create",
  LOGOUT: "/logout",
  // Routes from paths.js that were missing

  VISUALISATION_WITH_ID: "/:workspaceId?/explore/:pageType/:aggregateId",
  INTERNAL_DASHBOARD_GENERAL: "/:workspaceId?/internal-dashboard/general",
  INTERNAL_DASHBOARD: "/:workspaceId?/internal-dashboard/:tabType",
  ORG_DESIGN_SETTINGS: "/:workspaceId?/org-design-settings",
  USER_REPORT: "/:workspaceId?/user-report",
  REPORTS: process.env.REACT_APP_REPORTS_DOMAIN,
  DIRECTORY_USER_HOME_BASE_PATH: "/:workspaceId?/home",
  DIRECTORY_USER_HOME: "/:workspaceId?/home/:tabType",
  ADMIN: "/:workspaceId?/admin/dashboard",
  ADMIN_DASHBOARD: "/:workspaceId?/admin/dashboard/:tabType",
  TAG_DETAILS: "/:workspaceId?/tags/:id",
  API_DOCS: "/:workspaceId?/api-docs",
  LOGIN_HISTORY_PAGE: "/:workspaceId?/login-history",
  OBJECTIVES: "/:workspaceId?/objectives",
  // Workflow routes
  WORKFLOWS: "/:workspaceId?/workflows",
  WORKFLOW_BUILDER: "/:workspaceId?/workflows/:id",
  WORKFLOW_RUNS: "/:workspaceId?/workflows/:id/runs",
  WORKFLOW_RUN_DETAILS: "/:workspaceId?/workflows/:workflowId/runs/:runId",
  AI_DASHBOARDS: "/:workspaceId?/dashboards/:chatId/:dashboardId",
  TEAM_CANVAS: "/:workspaceId?/team-canvas",
  SLIDES: "/:workspaceId?/slides",
  SLIDE_DECK: "/:workspaceId?/slides/:deckId",
  DESIGNS: "/:workspaceId?/designs",
  DESIGN_DETAIL: "/:workspaceId?/designs/:id",
  DESIGN_EDIT: "/:workspaceId?/designs/:id/edit",
  AI_DASHBOARDS_V2: "/:workspaceId?/dashboards/v2/:dashboardId",
  AI_DASHBOARDS_LIBRARY: "/:workspaceId?/dashboards/library",
  // NOTE: This must always be the last path in the URL to avoid conflicts with other paths.
  APP_HOME: "/:workspaceId?",
};

// Exclude paths that should not be redirected to fallback
const excludedPaths = [URL.DATASOURCE];

// any urls that dont require authentication
export const PUBLIC_URLS = [URL.LOGOUT];

export const TEAM_TAB = {
  DETAILS: "details",
  ORG_CHART: "orgchart",
  INSIGHTS: "insights",
  MEMBERSHIPS: "memberships",
  STRATEGY: "strategy",
  ASSOCIATIONS: "associations",
  PRINCIPLES: "principles",
  PLANNER: "planner", // Deprecated. TODO: Temporarily added as per customer request. Should remove after 3-4 months.
  REQUEST_ALLOCATION_NUMBERS: "request-allocations",
  APPROVE_ALLOCATION_NUMBERS: "approve-allocations",
  INDIVIDUAL_ALLOCATIONS: "individual-allocations",
  INDIVIDUAL_ALLOCATIONS_PERSON: "individual-allocations-person",
  REQUEST_ALLOCATION_PEOPLE: "assign-to-teams",
  APPROVE_ALLOCATION_PEOPLE: "assign-to-groups",
  FORECAST: "forecast",
  WORKFORCE: "workforce",
  HISTORY: "history",
  METRICS: "metrics",
  CHANGELOG: "changelog",
};

export const PERSON_TAB = {
  DETAILS: "details",
  ALLOCATIONS: "allocations",
  USER_DASHBOARD: "dashboard",
  HISTORY: "history",
  LINE_MANAGEMENT: "line-management",
  SCOPE_OF_ACCESS: "scope-of-access",
};

export const POSITION_TAB = {
  DETAILS: "details",
  HISTORY: "history",
};

export const pathList = lodash.values(URL);

const normalizePathnameForMatch = (pathname) => {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.replace(/\/+$/, "");
  }
  return pathname;
};

export const matchPaths = (pathUrl, pathListToMatch = pathList) => {
  const pathname = stripFormatCharacters(pathUrl || window.location.pathname);

  if (Array.isArray(pathListToMatch)) {
    for (const path of pathListToMatch) {
      const match = matchPath(pathname, {
        path,
        exact: true,
        strict: true,
      });
      if (match) {
        return match;
      }
    }
    return null;
  }

  return matchPath(pathname, {
    path: pathListToMatch,
    exact: true,
    strict: true,
  });
};

export const workspaceIdFromUrl = (pathUrl) =>
  matchPaths(normalizePathnameForMatch(pathUrl || window.location.pathname))
    ?.params?.workspaceId;

/** Tag id from URL only on tag routes (not people/teams/goals :id params). */
export const tagIdFromUrl = (pathUrl) =>
  matchPaths(pathUrl, [URL.TAG_DETAILS, URL.TAG_EXPLORE])?.params?.id;

/** Team/group id from URL only on team routes (not tag/people/goals :id params). */
export const teamIdFromUrl = (pathUrl) =>
  matchPaths(pathUrl, [
    URL.TEAM_DETAILS_BASE_PATH,
    URL.TEAM_DETAILS,
    URL.TEAM_EXPLORE,
  ])?.params?.id;

/** Goal/objective id from URL only on goals explore routes. */
export const goalIdFromUrl = (pathUrl) => {
  const id = matchPaths(pathUrl, [URL.GOALS_EXPLORE])?.params?.id;
  return id || undefined;
};

/** True on /explore/goals with no selected objective id. */
export const isOnGoalsExploreRoot = (pathUrl) => {
  const matched = matchPaths(pathUrl, [URL.GOALS_EXPLORE]);
  if (!matched) {
    return false;
  }
  return !matched.params?.id;
};

const withoutFormatCharacters = (value) =>
  typeof value === "string" ? stripFormatCharacters(value) : value;

export const url = (path, params) => {
  const workspaceId = params?.workspaceId ?? workspaceIdFromUrl();
  const cleanParams = params
    ? Object.fromEntries(
        Object.entries(params).map(([key, value]) => [
          key,
          withoutFormatCharacters(value),
        ])
      )
    : undefined;
  return generatePath(path, {
    ...cleanParams,
    workspaceId: withoutFormatCharacters(workspaceId),
  });
};

const isPathExcludedFromFallback = (value) => {
  return lodash.includes(excludedPaths, value);
};

export const fallbackPath = (pathUrl) => {
  const currentPath = pathUrl || window.location.pathname;
  const pathListWithIdParam = lodash.filter(
    lodash.values(URL),
    (value) =>
      lodash.includes(value, ":id") && !isPathExcludedFromFallback(value)
  );
  const matchedPath = matchPaths(currentPath, pathListWithIdParam);
  if (matchedPath) {
    const fallback = lodash.replace(matchedPath.path, /\/:id.*/, "");
    return url(fallback);
  }
  return currentPath;
};

// Helper functions moved from paths.js
const getDecodedValue = (value) => {
  return value ? decodeURI(value) : null;
};

export const getSearchTerm = () =>
  decodeURI(matchPaths()?.params?.searchTerm || "");

export const getSearchPath = (type, searchTerm, useFallbackFromUrl = true) => {
  const urlParams = matchPaths()?.params;
  return generatePath(URL.SEARCH, {
    type: type || urlParams?.type || "all",
    searchTerm:
      searchTerm ||
      (useFallbackFromUrl ? getDecodedValue(urlParams?.searchTerm) : null),
    workspaceId: urlParams?.workspaceId,
  });
};

export const workspaceRootUrl = (workspaceId) =>
  generatePath(URL.APP_HOME, { workspaceId });

export const navigateToWorkspace = (workspaceId) => {
  window.location = workspaceRootUrl(workspaceId);
};

export const onNavigateUp = (group, history, groupTypes) => {
  const { parentIds } = group;
  const groupType = getGroupType(group, groupTypes);
  if (groupType.isRoot || lodash.isEmpty(parentIds)) {
    history.push(url(URL.EXPLORE));
  } else {
    history.push(
      url(URL.TEAM_DETAILS_BASE_PATH, { id: lodash.last(parentIds) })
    );
  }
};

export default URL;
